import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, SlidersHorizontal } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useLang } from "../../context/LangContext";
import { API } from "../../utils/format";
import { PercentInput } from "../../components/ui/PercentInput";
import { FieldHint } from "../../components/ui/FieldHint";
import AgentsDiagnosisPanel from "../../components/AgentsDiagnosisPanel";
import ModulePermissionsDialog, { AGENT_MODULES, WAREHOUSE_MODULES } from "../../components/ModulePermissionsDialog";



const AgentsManagement = () => {
  const { t } = useLang();
  const [agents, setAgents] = useState([]);
  const [pricelistTypes, setPricelistTypes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [formData, setFormData] = useState({ name: "", commission_rate: 0.09, access_code: "", pricelist_type_ids: [] });
  const [modulesFor, setModulesFor] = useState(null);
  const [whUsers, setWhUsers] = useState([]);

  const fetchWhUsers = async () => {
    try {
      const r = await axios.get(`${API}/admin/users`);
      setWhUsers((r.data || []).filter((u) => u.role === "warehouse"));
    } catch { /* silenzioso */ }
  };

  const saveModules = async (disabledList) => {
    try {
      if (modulesFor.type === "agent") {
        await axios.put(`${API}/agents/${modulesFor.entity.id}`, { disabled_modules: disabledList });
        fetchAgents();
      } else {
        await axios.put(`${API}/admin/users/${modulesFor.entity.id}/modules`, { disabled_modules: disabledList });
        fetchWhUsers();
      }
      toast.success("Permessi moduli aggiornati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio permessi");
      throw e;
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };
  const fetchPricelistTypes = async () => {
    try { const r = await axios.get(`${API}/pricelist-types`); setPricelistTypes(r.data || []); } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };

  useEffect(() => { fetchAgents(); fetchPricelistTypes(); fetchWhUsers(); }, []);

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        commission_override: formData.commission_override === "" || formData.commission_override == null ? null : parseFloat(formData.commission_override),
      };
      if (editAgent?.id) await axios.put(`${API}/agents/${editAgent.id}`, payload);
      else await axios.post(`${API}/agents`, payload);
      toast.success("Salvato");
      setIsDialogOpen(false);
      fetchAgents();
    } catch { toast.error("Errore"); }
  };

  const openEdit = (agent) => {
    setEditAgent(agent);
    setFormData({
      name: agent?.name || "",
      commission_rate: agent?.commission_rate || 0.09,
      commission_override: agent?.commission_override != null ? agent.commission_override : "",
      access_code: agent?.access_code || "",
      pricelist_type_ids: agent?.pricelist_type_ids || [],
      no_commission: !!agent?.no_commission,
    });
    setIsDialogOpen(true);
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/portal/${code}`);
    toast.success("Link copiato!");
  };

  const [creatingLoginFor, setCreatingLoginFor] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "", name: "" });
  const [warehouseDialog, setWarehouseDialog] = useState(false);
  const [warehouseForm, setWarehouseForm] = useState({ email: "", password: "", name: "" });

  const createAgentLogin = async () => {
    if (!loginForm.email || !loginForm.password) { toast.error("Email e password obbligatori"); return; }
    try {
      await axios.post(`${API}/admin/users`, { ...loginForm, name: loginForm.name || creatingLoginFor.name, role: "agent", agent_id: creatingLoginFor.id });
      toast.success("Login agente creato — l'agente può ora accedere a /login");
      setCreatingLoginFor(null);
      setLoginForm({ email: "", password: "", name: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const createWarehouseUser = async () => {
    if (!warehouseForm.email || !warehouseForm.password || !warehouseForm.name) { toast.error("Tutti i campi obbligatori"); return; }
    try {
      await axios.post(`${API}/admin/users`, { ...warehouseForm, role: "warehouse" });
      toast.success("Utente magazzino creato");
      setWarehouseDialog(false);
      setWarehouseForm({ email: "", password: "", name: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const impersonateAgent = async (agent) => {
    if (!agent.user_id) { toast.error("L'agente non ha un login attivo"); return; }
    if (!window.confirm(`Visualizzare l'app come '${agent.name}'?\nIl tuo accesso admin sarà sostituito temporaneamente.\nPer tornare admin, fai logout e rifai login.`)) return;
    try {
      const r = await axios.post(`${API}/admin/impersonate`, { user_id: agent.user_id });
      // Replace local token with impersonation token
      localStorage.setItem("token", r.data.token);
      localStorage.setItem("impersonating", "1");
      localStorage.setItem("impersonating_name", agent.name);
      toast.success(`Ora vedi l'app come ${agent.name}. Fai logout per tornare admin.`);
      // Force navigation
      window.location.href = "/agent/dashboard";
    } catch (e) { toast.error(e.response?.data?.detail || "Errore impersonificazione"); }
  };

  const downloadBordereau = async (agent) => {
    const now = new Date();
    const ym = window.prompt(`Bordereau provvigioni — formato YYYY-MM\nLascia vuoto per il mese corrente:`, `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    if (ym === null) return;
    const [y, m] = (ym || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`).split("-");
    if (!y || !m) { toast.error("Formato non valido (es. 2026-02)"); return; }
    try {
      const r = await axios.get(`${API}/agents/${agent.id}/bordereau?year=${y}&month=${parseInt(m, 10)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `Bordereau_${agent.access_code}_${y}_${m}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Bordereau generato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-semibold text-stone-900">{t.agents}</h2>
          <p className="text-stone-500 text-sm mt-1">{agents.length} agenti — Link portale per clienti</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setWarehouseDialog(true)} className="border-stone-300" data-testid="add-warehouse-user-btn">
            <Plus className="w-4 h-4 mr-2" />Utente Magazzino
          </Button>
          <Button onClick={() => openEdit(null)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="add-agent-btn">
            <Plus className="w-4 h-4 mr-2" />{t.new}
          </Button>
        </div>
      </div>

      {/* Diagnostica agenti (duplicati / orfani senza login) — fix bulk-import */}
      <AgentsDiagnosisPanel onDataChanged={fetchAgents} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {agents.map(agent => (
          <Card key={agent.id} className="bg-white border-stone-200 hover:border-[#C5A059] hover:shadow-md transition-all">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-stone-900">{agent.name}</h3>
                  <p className="text-sm text-[#8A1C2A] font-medium">{t.commission}: {agent.commission_override != null ? `${(agent.commission_override * 100).toFixed(1)}% (dedicata)` : `${(agent.commission_rate * 100).toFixed(1)}%`}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setModulesFor({ type: "agent", entity: agent })} className="h-8 w-8 p-0 hover:bg-stone-100" title="Permessi moduli" data-testid={`agent-modules-${agent.access_code}`}><SlidersHorizontal className="w-4 h-4 text-stone-500" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(agent)} className="h-8 w-8 p-0 hover:bg-stone-100" data-testid={`agent-edit-${agent.access_code}`}><Edit className="w-4 h-4 text-stone-500" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => axios.delete(`${API}/agents/${agent.id}`).then(fetchAgents)} className="h-8 w-8 p-0 hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
              <div className="bg-stone-50 rounded-md p-3 flex items-center justify-between border border-stone-200">
                <div>
                  <p className="text-xs text-stone-500">Codice: <span className="font-mono text-stone-900 font-semibold">{agent.access_code}</span></p>
                </div>
                <Button size="sm" variant="outline" onClick={() => copyLink(agent.access_code)} className="border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10">Copia Link</Button>
              </div>
              <div className="mt-2 flex justify-end gap-1 flex-wrap">
                {agent.user_id ? (
                  <>
                    <span className="text-xs text-green-600 flex items-center gap-1 mr-2">✓ Ha login attivo</span>
                    <Button size="sm" variant="outline" onClick={() => downloadBordereau(agent)} className="text-xs border-stone-300 text-stone-700" data-testid={`bordereau-${agent.access_code}`}>
                      📄 Bordereau
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => impersonateAgent(agent)} className="text-xs border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10" data-testid={`impersonate-${agent.access_code}`}>
                      👁️ Visualizza come
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setCreatingLoginFor(agent)} className="text-xs text-[#8A1C2A] hover:bg-[#8A1C2A]/5" data-testid={`create-login-${agent.access_code}`}>
                    🔑 Crea login agente
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Utenti Magazzino — permessi moduli */}
      {whUsers.length > 0 && (
        <Card className="bg-white border-stone-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-stone-900 mb-2">📦 Utenti Magazzino — permessi moduli</h3>
            <div className="divide-y divide-stone-100">
              {whUsers.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-2" data-testid={`wh-user-row-${u.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{u.name || u.email}</p>
                    <p className="text-xs text-stone-500">{u.email}{(u.disabled_modules || []).length > 0 && ` · ${u.disabled_modules.length} moduli disattivati`}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setModulesFor({ type: "warehouse", entity: u })} className="border-stone-300 text-xs h-8" data-testid={`wh-user-modules-${u.id}`}>
                    <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Moduli
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-white border-stone-200 max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-stone-900">{editAgent?.id ? t.edit : t.new} Agente</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-stone-700">Nome</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white border-stone-300" data-testid="agent-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700 flex items-center gap-1">
                Provvigione base % (fallback se nessun listino)
                <FieldHint>
                  <strong>Provvigione di default</strong> dell'agente, applicata quando non è collegato a nessun listino oppure il listino non specifica una commissione. Esempio: 9 = l'agente guadagna il 9% del venduto.
                </FieldHint>
              </Label>
              <PercentInput value={formData.commission_rate} onChange={(v) => setFormData({ ...formData, commission_rate: v })} data-testid="agent-commission-rate" />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700 flex items-center gap-1">
                Provvigione agente dedicata % (opzionale)
                <FieldHint>
                  <strong>Override personale dell'agente</strong>. Se impostato, sostituisce la commissione del listino. <br/><br/>
                  <strong>Ordine di priorità</strong> nel calcolo della provvigione:<br/>
                  1️⃣ Cliente → 2️⃣ Articolo → 3️⃣ Agente (questa) → 4️⃣ Listino<br/><br/>
                  Lascia vuoto per usare la commissione del listino primario.
                </FieldHint>
              </Label>
              <PercentInput value={formData.commission_override} onChange={(v) => setFormData({ ...formData, commission_override: v === 0 ? "" : v })} placeholder="Vuoto = usa la provvigione del listino" data-testid="agent-commission-override" />
              <p className="text-xs text-stone-500">Priorità provvigioni: Cliente → Articolo → Agente → Listino. Se impostata, vince sul listino (ma non su cliente/articolo).</p>
            </div>
            <label className="flex items-start gap-2 p-3 rounded-md border border-amber-200 bg-amber-50 cursor-pointer">
              <input
                type="checkbox"
                checked={!!formData.no_commission}
                onChange={(e) => setFormData({ ...formData, no_commission: e.target.checked })}
                className="mt-0.5 accent-[#8A1C2A]"
                data-testid="agent-no-commission-flag"
              />
              <span className="text-sm">
                <strong>🚫 No provvigioni</strong>
                <span className="block text-xs text-stone-600">Se attivo, per questo agente le provvigioni NON vengono mai calcolate, anche se usa listini con provvigione (vince su tutto).</span>
              </span>
            </label>
            <div className="space-y-2">
              <Label className="text-stone-700 font-semibold flex items-center gap-1">
                🏷️ Listini associati (ordine di priorità)
                <FieldHint>
                  Seleziona uno o più listini che l'agente può vendere. <strong>L'ordine conta</strong>: il primo listino selezionato è quello "primario" e determina la commissione base. Quando un cliente è associato ad un listino dell'agente, vede i prezzi di quel listino.
                </FieldHint>
              </Label>
              <p className="text-xs text-stone-500">L'agente prende la provvigione dal primo listino. Se un prodotto ha un override commissione su uno dei suoi listini, vince quello.</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {pricelistTypes.map(pt => {
                  const arr = formData.pricelist_type_ids || [];
                  const idx = arr.indexOf(pt.id);
                  const checked = idx >= 0;
                  return (
                    <button
                      type="button"
                      key={pt.id}
                      onClick={() => {
                        const next = checked ? arr.filter(x => x !== pt.id) : [...arr, pt.id];
                        setFormData({ ...formData, pricelist_type_ids: next });
                      }}
                      className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors flex items-center gap-1 ${checked ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : "bg-white border-stone-300 text-stone-700 hover:border-[#8A1C2A]"}`}
                      data-testid={`agent-pl-${pt.id}`}
                    >
                      {checked && <span className="bg-white text-[#8A1C2A] rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">{idx + 1}</span>}
                      {pt.name} ({(pt.commission * 100).toFixed(1)}%)
                    </button>
                  );
                })}
              </div>
              {(formData.pricelist_type_ids || []).length > 0 && (
                <p className="text-xs text-[#C5A059] mt-1">⭐ Primario: <strong>{pricelistTypes.find(p => p.id === formData.pricelist_type_ids[0])?.name}</strong></p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-stone-300">{t.cancel}</Button>
            <Button onClick={handleSave} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="agent-save-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Agent Login */}
      <Dialog open={!!creatingLoginFor} onOpenChange={() => setCreatingLoginFor(null)}>
        <DialogContent className="bg-white border-stone-200">
          <DialogHeader><DialogTitle>🔑 Crea login per {creatingLoginFor?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">L'agente potrà accedere da <strong>/login</strong> e vedere il suo portale con provvigioni, ordini e clienti.</p>
          <div className="space-y-3">
            <div><Label>Nome visualizzato</Label><Input value={loginForm.name} onChange={e => setLoginForm({ ...loginForm, name: e.target.value })} placeholder={creatingLoginFor?.name} className="bg-white border-stone-300" /></div>
            <div><Label>Email *</Label><Input value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="agente@email.it" className="bg-white border-stone-300" data-testid="agent-login-email" /></div>
            <div><Label>Password *</Label><Input type="text" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="almeno 6 caratteri" className="bg-white border-stone-300" data-testid="agent-login-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatingLoginFor(null)} className="border-stone-300">Annulla</Button>
            <Button onClick={createAgentLogin} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="save-agent-login-btn">Crea login</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Warehouse User */}
      <Dialog open={warehouseDialog} onOpenChange={setWarehouseDialog}>
        <DialogContent className="bg-white border-stone-200">
          <DialogHeader><DialogTitle>📦 Nuovo utente Magazzino</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600">Accederà da /login e potrà inserire pesi reali, segnare disponibilità e gestire date di spedizione.</p>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={warehouseForm.name} onChange={e => setWarehouseForm({ ...warehouseForm, name: e.target.value })} placeholder="es. Mario Magazzino" className="bg-white border-stone-300" data-testid="warehouse-user-name" /></div>
            <div><Label>Email *</Label><Input value={warehouseForm.email} onChange={e => setWarehouseForm({ ...warehouseForm, email: e.target.value })} placeholder="magazzino@giotto.it" className="bg-white border-stone-300" data-testid="warehouse-user-email" /></div>
            <div><Label>Password *</Label><Input type="text" value={warehouseForm.password} onChange={e => setWarehouseForm({ ...warehouseForm, password: e.target.value })} placeholder="almeno 6 caratteri" className="bg-white border-stone-300" data-testid="warehouse-user-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWarehouseDialog(false)} className="border-stone-300">Annulla</Button>
            <Button onClick={createWarehouseUser} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="save-warehouse-user-btn">Crea utente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permessi moduli agente / magazzino */}
      <ModulePermissionsDialog
        open={!!modulesFor}
        onClose={() => setModulesFor(null)}
        title={modulesFor?.entity?.name || modulesFor?.entity?.email || ""}
        modules={modulesFor?.type === "warehouse" ? WAREHOUSE_MODULES : AGENT_MODULES}
        initialDisabled={modulesFor?.entity?.disabled_modules || []}
        onSave={saveModules}
      />
    </div>
  );
};



export default AgentsManagement;

